/**
 * Editor-only CSS injected into the canvas iframe.
 *
 * **It never touches the compiled body.** The canvas renders the same bytes the
 * server sends, and that equality is the whole reason WYSIWYG here can't drift —
 * so anything that exists purely for editing is layered on at render time, in
 * this one place, and is absent from `compile()` output.
 *
 * What it solves: **an empty container slot has no geometry.** A fresh
 * `core.columns` renders its columns at 292×**0** px, and both the canvas
 * hit-testing (`dropTargetAtPoint` intersects slot rects) and the drop indicator
 * work from measured rects — so without this, an empty column cannot be dropped
 * into at all. Giving those slots a minimum height in the *editor* makes them
 * targets without changing what recipients get: a min-height in the component
 * itself would ship dead space in every real email.
 */
import { Block } from './document';
import { slotsOf } from './tree';

/** Height an empty slot is given so it can be measured and aimed at. */
export const EMPTY_SLOT_MIN_HEIGHT = 44;

/**
 * `<blockId>:<slotIndex>` for every slot with no children, at any depth.
 *
 * Derived from the document rather than from the DOM: a marker for a slot the
 * component doesn't render (`core.columns` in `two` layout never renders `col3`)
 * simply matches nothing, which is harmless — whereas reading the DOM would need
 * the frame to be measured before it could be styled.
 */
export function emptySlotKeys(blocks: Block[], into: string[] = []): string[] {
  for (const block of blocks) {
    slotsOf(block).forEach((children, index) => {
      if (children.length === 0) {
        into.push(`${block.id}:${index}`);
      } else {
        emptySlotKeys(children, into);
      }
    });
  }
  return into;
}

/** CSS-escapes a marker value for use in an attribute selector. */
function quoteAttr(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * The editor stylesheet for one document state.
 *
 * Returns an empty string when there is nothing to add. The canvas render still carries
 * one editor-only `<meta>` stamp — see `canvasStamp` — but the **stored body** never does,
 * which is the equality that matters.
 */
export function editorCanvasCss(emptySlots: string[]): string {
  if (emptySlots.length === 0) return '';

  const selector = emptySlots
    .map((key) => `[data-cvn-slot="${quoteAttr(key)}"]`)
    .join(',');

  // `!important` because the component's own inline styles would otherwise win,
  // and a slot region is exactly the element components style most tightly.
  return `${selector}{min-height:${EMPTY_SLOT_MIN_HEIGHT}px !important;box-sizing:border-box !important;outline:1px dashed rgba(0,0,0,0.25);outline-offset:-1px;}`;
}

/** Marks the injected block so it is recognizable in a DOM dump. */
const STYLE_OPEN = '<style data-cvn-editor="1">';

/**
 * Identifies *which* markup the canvas is currently showing.
 *
 * The overlay must not paint selection chrome from rects that describe a previous
 * document. Comparing against the srcDoc React last handed the iframe is not enough: the
 * attribute is set immediately while the document is laid out asynchronously, so a
 * measurement pass can read the **old** layout and still look current. A stamp carried
 * *inside* the rendered document is the only thing that answers "what is on screen".
 */
export const CANVAS_STAMP_ATTRIBUTE = 'data-cvn-stamp';

/** Cheap and sufficient: a collision only ever means "treated as fresh". */
export function canvasStamp(html: string): string {
  return String(html.length);
}

/**
 * Keeps a link click in **Preview** from replacing the preview.
 *
 * The design canvas is covered by the selection overlay, so its links are never
 * reachable. Preview has no overlay — the links are live, and a `srcdoc` frame
 * resolves them against the *app's* URL, so clicking one loads a page of backoffice
 * (or a 404) where the email was. `target="_blank"` sends the click out of the frame
 * instead; the sandbox has no `allow-popups`, so nothing actually opens and the
 * preview simply stays put, which is the behaviour a preview should have.
 *
 * `<base>` carries **no `href`**: adding one would re-resolve every relative URL in
 * the email and change what the preview shows.
 */
const PREVIEW_BASE = '<base target="_blank">';

/** Adds the editor stylesheet to a rendered document. */
export function injectEditorStyles(html: string, css: string): string {
  const stamp = `<meta ${CANVAS_STAMP_ATTRIBUTE}="${canvasStamp(html)}">`;
  return injectIntoHead(html, css ? `${stamp}${STYLE_OPEN}${css}</style>` : stamp);
}

/** The rendered email as Preview shows it: unchanged except that links go nowhere. */
export function preparePreviewHtml(html: string): string {
  return injectIntoHead(html, PREVIEW_BASE);
}

/**
 * Inserts before `</head>` when there is one — the compiled emails have a real head,
 * and a late `<style>` or `<base>` in `<body>` is valid but reparented by the parser,
 * which has bitten enough people to be worth avoiding. Falls back to appending.
 */
function injectIntoHead(html: string, tag: string): string {
  const headClose = html.toLowerCase().lastIndexOf('</head>');
  if (headClose !== -1) {
    return html.slice(0, headClose) + tag + html.slice(headClose);
  }
  return html + tag;
}

/**
 * A 1×1 transparent GIF — what an image with nothing in it points at.
 */
const BLANK_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Neutralises `src=""` before the HTML reaches the canvas iframe.
 *
 * An empty `src` is not "no image": per the HTML spec it resolves to the *document's
 * own URL*, so the iframe requested the whole backoffice page as an image on every
 * render — a 404 in the console and a full HTML document down the wire per empty
 * image. An image block starts with no `src`, so a partner hit this the moment they
 * dragged one in. Measured with request tracing: one `resourceType: image` request to
 * the page URL, `naturalWidth === 0`.
 *
 * Done here rather than in the component: the `core/image` Twig lives in the backend
 * fragment catalog, and the compiled body is deliberately never rewritten — this is
 * the editor's own preview, where the request is wasted, and the placeholder is also
 * what makes an empty image visible as a box to click.
 */
export function blankEmptyImageSources(html: string): string {
  return html.replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(""|'')/gi,
    (_match, prefix) => `${prefix}"${BLANK_PIXEL}"`,
  );
}
